import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";
import uniq from "lodash/uniq";
import classnames from "classnames";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { useProductDetailContext } from "@contexts/product-detail-context";
import { useProductInfoContext } from "@contexts/product-info-context";
import useGetCartAddedVariant from "@hooks/product-detail/use-get-cart-added-variant";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { useFlags } from "@stadium/services/launch-darkly";
import { variantLevelMoqFlag } from "@stadium/services/launch-darkly/constants";

import {
  getProductOptionTypesData,
  getProductVariantsData,
  processVariantImages,
  getVariantDiscountTableData,
  getDefaultIndexIfSingleVariantAvailable,
  sortInvalidColorLast,
  getProductImageAlt,
} from "@helpers/product-details";
import { getMinOrderableQuantity } from "@helpers/products/search";

import ProductCarousel from "@components/store/product-detail/shared/product-detail-carousel";
import SwagProductDetail from "@components/store/product-detail/swag/swag-product-detail";
import Multimedia from "@components/shared/multimedia";
import PaginationSlider from "@stadium/ui/src/components/pagination-slider";
import RadioInput from "@stadium/ui/src/components/form/RadioInput";
import Label from "@stadium/ui/src/components/form/Label";

import { getIsLockerShop } from "@selectors/store";

import { DEFAULT_QUANTITY } from "@constants/products/detail";

import styles from "./main.module.scss";
import modalStyles from "./product-detail-modal.module.scss";

const Swag = ({ product, masterVariant, canBuyProduct, variantId }) => {
  const { modalClasses, selectedProductImg, selectedVariantSliderImages } = useProductInfoContext();
  const { productImgClass = "", productContentClass = "" } = modalClasses;

  const { [variantLevelMoqFlag]: variantLevelMoqFlagEnabled } = useFlags();

  const { isSendToMyself } = useSendItemsContext();
  const { editProductIds } = useProductDetailContext();
  const { isSwagCreationShopFlow, preferredColorsHex } = useCreateSwagContext();
  const { intent } = product;
  const [optionTypeVariantMapping, setOptionTypeVariantMapping] = useState({});
  const [allSizesVariants, setAllSizesVariants] = useState([]);
  const [selectedSizeVariant, setSelectedSizeVariant] = useState({});
  const [sizeQtyMapping, setSizeQtyMapping] = useState({});
  const [productQuantity, setProductQuantity] = useState(DEFAULT_QUANTITY);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { setIsOptionSelected, setIsColorSelected } = useProductDetailContext();
  const [selectedPreferredColors, setSelectedPreferredColors] = useState(
    product?.selectedColorOptionsForAutomatedDesign?.map((color) => color.id) || [],
  );
  const [newSwagDisplayImage, setNewSwagDisplayImage] = useState({});
  const optionTypesData = useMemo(() => getProductOptionTypesData(product), [product]);
  const isLockerShop = useSelector(getIsLockerShop);

  const { hasTierPricing } = product;
  const selectBulkOptionInEditMode = useMemo(
    () => !isLockerShop && editProductIds?.length > 1 && hasTierPricing,
    [isLockerShop, editProductIds, hasTierPricing],
  );

  const handleSwagCreationImgChange = (color) => {
    setNewSwagDisplayImage(color);
  };

  const cartAddedVariant = useGetCartAddedVariant({ intent });
  const getDefaultSelectedVariantIndex = (variants, variantsData) => {
    if (cartAddedVariant) {
      // update product from cart
      return variants.findIndex((variant) => {
        if (!isEmpty(variantsData[variant].variants))
          return variantsData[variant].variants.find(
            ({ externalId }) => cartAddedVariant?.productId?.toString() === externalId,
          );
        return variantsData[variant].externalId === cartAddedVariant?.productId?.toString();
      });
    } else if (variantId) {
      // for variant as product feature, select variant by default based on variantId
      return variants.findIndex((variant) => variantsData[variant].id === variantId);
    } else if (variants.length === 1) {
      // If product has only single variant
      return 0;
    } else {
      return getDefaultIndexIfSingleVariantAvailable(variants, variantsData);
    }
  };

  const setDefaultSelectedVariant = (variant) => {
    if (variant) {
      let defaultSecondaryVariant;
      if (cartAddedVariant) {
        defaultSecondaryVariant = (variant.variants || []).find(
          ({ externalId }) => externalId === cartAddedVariant?.productId.toString(),
        );
        setProductQuantity(cartAddedVariant.quantity);
      } else {
        const sizesAvailable = (variant.variants || []).filter(({ outOfStock }) => !outOfStock);
        if (sizesAvailable.length === 1) defaultSecondaryVariant = sizesAvailable[0];
      }
      // for bulk products we are using sizeQtyMapping so no need to select size variant
      if (!selectBulkOptionInEditMode && defaultSecondaryVariant && !product.bulkAvailable) {
        setSelectedSizeVariant(defaultSecondaryVariant);
        setIsOptionSelected(true);
      }

      setOptionTypeVariantMapping(variant);
      setIsColorSelected(true);
    } else if (!isSendToMyself) {
      // This has been added for send to others case where we have only master variant.
      setProductQuantity(cartAddedVariant?.quantity);
    }
  };

  const variantsData = useMemo(() => getProductVariantsData(optionTypesData), [optionTypesData]);

  const colorVariantData = useMemo(
    () => sortBy(Object.values(variantsData), "position").sort(sortInvalidColorLast),
    [variantsData],
  );

  useEffect(() => {
    if (!isSwagCreationShopFlow) return;

    const preferredColorIds = colorVariantData
      .filter((color) => preferredColorsHex.includes(color.name))
      .map((color) => color.id);

    const selectedColorOptions =
      product?.selectedColorOptionsForAutomatedDesign?.map((color) => color.id) || [];

    setSelectedPreferredColors(uniq([...preferredColorIds, ...selectedColorOptions]));
  }, [isSwagCreationShopFlow, colorVariantData, preferredColorsHex, product?.id]);

  const setAllOrderedSizeVariants = (products) => {
    setAllSizesVariants(sortBy(products, "position"));
  };

  useEffect(() => {
    setNewSwagDisplayImage(colorVariantData[0]);
  }, []);

  const displayImage = isSwagCreationShopFlow
    ? newSwagDisplayImage?.image || product?.variantImages?.[0]
    : selectedProductImg;

  useEffect(() => {
    if (isEmpty(allSizesVariants) && optionTypesData.size && optionTypesData.color)
      setAllOrderedSizeVariants(
        uniqBy(optionTypesData.size, ({ id }) => id).map((item) => ({
          ...item,
          outOfStock: false,
        })),
      );
  }, [allSizesVariants, optionTypesData]);

  useEffect(() => {
    const variants = Object.keys(variantsData);
    if (!isEmpty(variants)) {
      const defaultSelected = getDefaultSelectedVariantIndex(variants, variantsData);
      setDefaultSelectedVariant(variantsData[variants[defaultSelected]]);
    } else if (masterVariant) {
      setDefaultSelectedVariant({
        ...masterVariant,
        images: processVariantImages(masterVariant.images),
      });
    }
  }, [variantsData, optionTypesData, cartAddedVariant, masterVariant]);

  const { isMobileDevice } = useContext(CurrentDeviceContext);

  // reset product quantity on variant change (prefer selected size MOQ when present so child state is not overwritten)
  useEffect(() => {
    const variants = product?.variantsIncludingMaster || [];
    const globalMin = Number(getMinOrderableQuantity(variants));
    const selectedVariantMinQty = Number(optionTypeVariantMapping?.minOrderableQuantity);
    const minQty =
      variantLevelMoqFlagEnabled && product.variantLevelCommerce
        ? selectedVariantMinQty
        : globalMin;
    if (isEmpty(editProductIds)) {
      setProductQuantity(minQty || DEFAULT_QUANTITY);
    }
    setSelectedImageIndex(0);
  }, [
    optionTypeVariantMapping,
    selectedSizeVariant,
    editProductIds,
    product?.variantsIncludingMaster,
    variantLevelMoqFlagEnabled,
  ]);

  useEffect(() => {
    if (!isEmpty(allSizesVariants) && !isEmpty(optionTypeVariantMapping?.variants)) {
      // upadate sizeVariants as per current selected color
      // if no variant is there assume it as OOS
      const updatedSizeVariants = uniqBy(
        [
          ...optionTypeVariantMapping.variants,
          ...allSizesVariants.map((item) => ({
            ...item,
            outOfStock: true,
          })),
        ],
        ({ id }) => id,
      );

      setAllOrderedSizeVariants(updatedSizeVariants);
      if (!isEmpty(selectedSizeVariant)) {
        // resetting size variant value accroding to updated optionType
        setSelectedSizeVariant(updatedSizeVariants.find(({ id }) => id === selectedSizeVariant.id));
      }
    }
  }, [optionTypeVariantMapping]);

  const discountTableData = useMemo(
    () =>
      getVariantDiscountTableData({
        masterVariant: product.masterVariant,
      }),
    [product],
  );

  const getSliderImages = () => {
    const variants = Object.keys(variantsData);
    if (!isEmpty(selectedSizeVariant)) return selectedSizeVariant.images;
    if (!isEmpty(optionTypeVariantMapping?.images)) return optionTypeVariantMapping.images;
    // select first variant image if nothing is selected
    if (!isEmpty(variantsData?.[variants[0]]?.images || []))
      return variantsData[variants[0]]?.images;
    if (!isEmpty(masterVariant?.images || [])) return processVariantImages(masterVariant.images);
    if (!isEmpty(product.variantImages)) return processVariantImages(product.variantImages);
    return [];
  };

  const sliderImages = useMemo(
    () => getSliderImages(),
    [selectedSizeVariant, optionTypeVariantMapping, variantsData],
  );
  const selectedVariantColor = newSwagDisplayImage?.presentation || "";
  const imageAlt = getProductImageAlt({
    name: product.name,
    color: isSwagCreationShopFlow
      ? selectedVariantColor
      : optionTypeVariantMapping?.presentation || selectedVariantColor,
  });

  const renderSwagCreationSlider = () => {
    return (
      <div className={modalStyles.productSlider}>
        <PaginationSlider
          sliderSettings={{
            dots: false,
            infinite: false,
            speed: 500,
            verticalSwiping: true,
            vertical: true,
            slidesToShow: 5,
            arrows: colorVariantData.length > 5,
          }}
        >
          {colorVariantData.map((color) => (
            <RadioInput
              key={color.id}
              value={color.id}
              id={`swag-creation-color-${color.id}`}
              name={`swag-creation-color-${product.id}`}
              checked={color.id === newSwagDisplayImage.id}
              onChange={() => handleSwagCreationImgChange(color)}
              label={
                <Label showIcon={false}>
                  <Multimedia
                    type="image"
                    src={color.image}
                    alt={color.presentation}
                    isNextImage={false}
                  />
                </Label>
              }
            />
          ))}
        </PaginationSlider>
      </div>
    );
  };

  const renderProductCarousel = () =>
    selectedProductImg || isSwagCreationShopFlow ? (
      <>
        <div className={styles.variantProductImg}>
          <Multimedia
            type="image"
            src={displayImage}
            alt={imageAlt}
            isNextImage={false}
            key={displayImage}
          />
        </div>
        {isSwagCreationShopFlow && (
          <div className={modalStyles.selectedVariantColor}>{selectedVariantColor}</div>
        )}
      </>
    ) : (
      <ProductCarousel
        sliderImages={selectedVariantSliderImages || sliderImages}
        selectedImageIndex={selectedImageIndex}
        setSelectedImageIndex={setSelectedImageIndex}
        imageMagnify
        productId={product.id}
        product={product}
        carouselContainerClass={styles.withVerticalSlider}
        thumbnailContainterClass={styles.thumbBlock}
        imageAlt={imageAlt}
      />
    );

  const renderMobileView = ({ renderHeader, renderMobileProductDetails }) => {
    return (
      <>
        {renderHeader()}
        <div className={`${styles.productCarousalWrapper} ${productImgClass}`}>
          {renderProductCarousel()}
        </div>
        <article className={`${styles.productDetail}  ${productContentClass}`}>
          <div className={styles.productDetailBox}>{renderMobileProductDetails()}</div>
        </article>
      </>
    );
  };

  const renderProductDetails = () => (
    <SwagProductDetail
      product={product}
      allSizesVariants={allSizesVariants}
      selectedSizeVariant={selectedSizeVariant}
      setSelectedSizeVariant={setSelectedSizeVariant}
      optionTypesData={optionTypesData}
      variantsData={variantsData}
      optionTypeVariantMapping={optionTypeVariantMapping}
      setOptionTypeVariantMapping={setOptionTypeVariantMapping}
      productQuantity={productQuantity}
      setProductQuantity={setProductQuantity}
      discountTableData={discountTableData}
      sizeQtyMapping={sizeQtyMapping}
      setSizeQtyMapping={setSizeQtyMapping}
      showProductName
      canBuyProduct={canBuyProduct}
      editProductIds={editProductIds}
      selectBulkOptionInEditMode={selectBulkOptionInEditMode}
      renderMobileView={renderMobileView}
      selectedPreferredColors={selectedPreferredColors}
      setSelectedPreferredColors={setSelectedPreferredColors}
    />
  );

  if (isMobileDevice) {
    return (
      <div>
        <div className={`${styles.rowProductDetail} ${styles.rowWithV2Enabled}`}>
          {renderProductDetails()}
        </div>
      </div>
    );
  }

  return (
    <div
      data-type="product-row"
      className={`${styles.rowProductDetail} ${styles.rowWithV2Enabled} ${
        isSwagCreationShopFlow ? modalStyles.swagProductRow : ""
      }`}
    >
      {isSwagCreationShopFlow && renderSwagCreationSlider()}

      <div
        className={`${styles.productCarousalWrapper} ${productImgClass} ${
          isSwagCreationShopFlow ? modalStyles.swagProductWrapper : ""
        } product-carousal-wrapper`}
      >
        {renderProductCarousel()}
      </div>
      <article
        className={classnames(styles.productDetail, productContentClass, "product-detail", {
          [modalStyles.swagProductDetail]: isSwagCreationShopFlow,
        })}
      >
        <div className={styles.productDetailBox}>{renderProductDetails()}</div>
      </article>
    </div>
  );
};

export default Swag;
